import { inject, Injectable, OnDestroy } from '@angular/core';
import { find, matches } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { combineLatest, merge, NEVER, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators';
import { eventFromBatch$ } from '../../../cross-team-shared/event-tracking/event-batch-values';
import { areDraftSavesEligible } from '../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { TaskPausingService } from '../task-pausing/task-pausing.service';
import { TimerService } from '../timer-badge/timer.service';

@Injectable()
export class IdleTimeoutService
  extends InjectableSuperclass
  implements OnDestroy
{
  private subscription?: Subscription;
  private timerService = inject(TimerService);
  private store = inject(TaskWorkspaceStore);
  private taskPausingService = inject(TaskPausingService);

  start() {
    const isEligibleForIdleTimeouts$ = this.isEligibleForIdleTimeout$();

    const isInactive$ = combineLatest([
      isEligibleForIdleTimeouts$,
      this.store('project')('steps').$,
    ]).pipe(
      switchMap(([isEligible, steps]) => {
        const step = find(steps, matches({ id: this.store.state().stepId }));
        if (!isEligible || !step?.auto_time_out_duration_enabled) {
          return NEVER;
        }
        return merge(
          this.store('lastInteractiveEventTimestamp').$,
          eventFromBatch$,
        ).pipe(
          debounceTime(step.auto_time_out_duration * 1000),
          map(() => step.auto_time_out_duration * 1000),
        );
      }),
    );

    this.subscription ??= isInactive$.subscribe(
      this.triggerIdleStatus.bind(this),
    );
  }

  override unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  private isEligibleForIdleTimeout$() {
    const canSaveDraft$ = this.store('outputInfoByName').$.pipe(
      map(areDraftSavesEligible),
    );
    const isTimerRunning$ = this.timerService.status$.pipe(
      map((status) => status === 'running'),
    );
    return combineLatest([canSaveDraft$, isTimerRunning$]).pipe(
      map(([canSaveDraft, isTimerRunning]) => canSaveDraft && isTimerRunning),
      distinctUntilChanged(),
    );
  }

  private triggerIdleStatus(autoTimeOutDuration: number) {
    const pauseTimestamp = this.store.state().lastInteractiveEventTimestamp;
    this.taskPausingService.pause(
      {
        method: 'auto_timeout',
        auto_time_out_duration: autoTimeOutDuration,
      },
      pauseTimestamp,
    );
  }
}
